import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { InputFieldComponent } from '../../../../shared/components/input-field/input-field';
import { CustomValidators } from '../../../../shared/validators/validators';
import { AuthService } from '../../../../shared/services/auth.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { LoginService} from '../../services/login.service';
import { AUTH_MESSAGES, SYSTEM_MESSAGES } from '../../../../shared/constants/message.constants';
import { HTTP_STATUS_CODES } from '../../../../shared/constants/error.constants';
import { LoginRequest } from '../../../../shared/interfaces/login.interface';
import { TenantService } from '../../../../shared/services/tenant.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, InputFieldComponent],
  templateUrl: './login.html'
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup = new FormGroup({});
  emailError: string = '';
  passwordError: string = '';
  emailHasError: boolean = false;
  passwordHasError: boolean = false;
  isLoading: boolean = false;
  private subscriptions: Subscription[] = [];
  
    constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private notificationService: NotificationService,
    private loginService: LoginService,
    private router: Router,
    private tenantService: TenantService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    this.loginForm = this.fb.group({
      email: ['', [CustomValidators.required, CustomValidators.emailFormat]],
      password: ['', [CustomValidators.required]]
    });
  }
  
  onEmailChange(value: string) {
    this.loginForm.patchValue({ email: value });
    if (this.emailHasError && value.trim() !== '') {
      this.emailHasError = false;
      this.emailError = '';
    }
  }
  
  onPasswordChange(value: string) {
    this.loginForm.patchValue({ password: value });
    if (this.passwordHasError && value.trim() !== '') {
      this.passwordHasError = false;
      this.passwordError = '';
    }
  }

  onEmailErrorClear() {
    this.emailError = '';
    this.emailHasError = false;
  }

  onPasswordErrorClear() {
    this.passwordError = '';
    this.passwordHasError = false;
  }

  private validateEmail(): void {
    const emailControl = this.loginForm.get('email');
    if (emailControl?.errors) {
      this.emailHasError = true;
      if (emailControl.errors['required']) {
        this.emailError = 'Email is required';
      } else if (emailControl.errors['emailFormat']) {
        this.emailError = 'Please enter a valid email format';
      }
    }
  }

  private validatePassword(): void {
    const passwordControl = this.loginForm.get('password');
    if (passwordControl?.errors) {
      this.passwordHasError = true;
      if (passwordControl.errors['required']) {
        this.passwordError = 'Password is required';
      } 
      // else if (passwordControl.errors['minLength']) {
      //   this.passwordError = 'Password must be at least 8 characters';
      // } else if (passwordControl.errors['passwordStrength']) {
      //   this.passwordError = 'Password must contain uppercase, lowercase, and number';
      // }
    }
  }

  private clearErrors(): void {
    this.emailError = '';
    this.passwordError = '';
    this.emailHasError = false;
    this.passwordHasError = false;
  }
  
  onSubmit() {
    this.clearErrors();
    this.validateEmail();
    this.validatePassword();

    if (this.loginForm.valid) {
      this.isLoading = true;
      const loginRequest: LoginRequest = {
        email: this.loginForm.value.email,
        password: this.loginForm.value.password
      };
      
      this.subscriptions.push(
        this.loginService.login(loginRequest).subscribe({
          next: (response) => {
            this.isLoading = false;
            
            if (response.isSuccess && response.data) {
              this.authService.setTokens(response.data);
              this.tenantService.setTenants(response.data.tenants);
              const tenantName = this.tenantService.primaryTenantName();
              if (!tenantName) {
                this.notificationService.showFailure('No tenant is assigned to this account.');
                this.authService.logoutSync();
                return;
              }
              sessionStorage.setItem('open_most_recent_chat', 'true');
              this.notificationService.showSuccess(AUTH_MESSAGES.LOGIN_SUCCESS);
              this.router.navigate(this.tenantService.chatCommands(tenantName));
            } else {
              this.notificationService.showFailure(response.message || AUTH_MESSAGES.LOGIN_ERROR);
            }
          },
          error: (error: HttpErrorResponse) => {
            this.isLoading = false;
            this.notificationService.showFailure(this.resolveLoginErrorMessage(error));
          }
        })
      );
    }
  }

  private resolveLoginErrorMessage(error: HttpErrorResponse): string {
    const serverMessage = error.error?.message;
    if (serverMessage) {
      return serverMessage;
    }

    switch (error.status) {
      case HTTP_STATUS_CODES.UNAUTHORIZED:
        return AUTH_MESSAGES.INVALID_CREDENTIALS;
      case 429:
        return AUTH_MESSAGES.ACCOUNT_LOCKED;
      case 0:
        return AUTH_MESSAGES.NETWORK_ERROR;
      default:
        return SYSTEM_MESSAGES.ERROR;
    }
  }

  openForgotPasswordModal() {
    this.router.navigate(['/forgot-password']);
  }


  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
